import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { CloseReadingExercise } from '../components/close-reading';
import { Plus, FileText, Loader2, BookOpen, Target, Award, TrendingUp } from 'lucide-react';

interface UserText {
  id: string;
  aiModel: string | null;
  aiPrompt: string | null;
  sourceDescription: string | null;
  aiText: string;
  createdAt: string;
}

interface Category {
  id: string;
  group_name: string;
  name: string;
  label: string;
  description: string | null;
  color: string | null;
}

interface Stats {
  totalSubmissions: number;
  completed: number;
  averageScore: number | null;
  categoriesUsed: number;
  totalCategories: number;
  userTextsSubmitted: number;
}

export default function CloseReadingPractice() {
  const { fetchApi } = useApi();
  const [categories, setCategories] = useState<Record<string, Category[]>>({});
  const [userTexts, setUserTexts] = useState<UserText[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [selectedText, setSelectedText] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showSubmitForm, setShowSubmitForm] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    aiText: '',
    aiModel: '',
    aiPrompt: '',
    sourceDescription: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [catRes, textsRes, statsRes] = await Promise.all([
        fetchApi<{ categories: Record<string, Category[]> }>('/api/close-reading/categories'),
        fetchApi<{ userTexts: UserText[] }>('/api/close-reading/user-texts'),
        fetchApi<{ stats: Stats }>('/api/close-reading/stats')
      ]);
      setCategories(catRes.categories);
      setUserTexts(textsRes.userTexts || []);
      setStats(statsRes.stats);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitText = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.aiText.trim()) return;

    setSubmitting(true);
    try {
      const response = await fetchApi<{ userText: { id: string } }>('/api/close-reading/user-texts', {
        method: 'POST',
        body: JSON.stringify(formData)
      });
      // Reload and select the new text
      const textData = await fetchApi<{ text: any }>(`/api/close-reading/user-texts/${response.userText.id}`);
      setSelectedText(textData.text);
      setShowSubmitForm(false);
      setFormData({ aiText: '', aiModel: '', aiPrompt: '', sourceDescription: '' });
      loadData();
    } catch (err) {
      console.error('Failed to submit text:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSelectText = async (textId: string) => {
    try {
      const response = await fetchApi<{ text: any }>(`/api/close-reading/user-texts/${textId}`);
      setSelectedText(response.text);
    } catch (err) {
      console.error('Failed to load text:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
      </div>
    );
  }

  if (selectedText) {
    // Create a pseudo-exercise from user text
    const exercise = {
      id: selectedText.id,
      title: selectedText.sourceDescription || 'Your AI Text',
      ai_text: selectedText.aiText,
      ai_model: selectedText.aiModel,
      ai_prompt: selectedText.aiPrompt,
      context_notes: null,
      min_annotations: 3, // Lower threshold for practice
      difficulty: 'practice'
    };

    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <button
          onClick={() => setSelectedText(null)}
          className="text-gray-400 hover:text-gray-200 mb-4"
        >
          ← Back to texts
        </button>
        <CloseReadingExercise
          exercise={exercise}
          categories={categories}
          mode="AUDIT"
        />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Close Reading Practice</h1>
          <p className="text-gray-400 mt-1">
            Analyze AI outputs you've encountered in the wild
          </p>
        </div>
        <button
          onClick={() => setShowSubmitForm(!showSubmitForm)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add AI Text
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 text-gray-400 mb-1">
              <Target className="w-4 h-4" />
              <span className="text-xs">Analyses</span>
            </div>
            <p className="text-2xl font-bold text-gray-100">{stats.completed}</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 text-gray-400 mb-1">
              <TrendingUp className="w-4 h-4" />
              <span className="text-xs">Avg Score</span>
            </div>
            <p className="text-2xl font-bold text-gray-100">
              {stats.averageScore ? `${stats.averageScore}%` : '—'}
            </p>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 text-gray-400 mb-1">
              <Award className="w-4 h-4" />
              <span className="text-xs">Categories</span>
            </div>
            <p className="text-2xl font-bold text-gray-100">
              {stats.categoriesUsed}/{stats.totalCategories}
            </p>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 text-gray-400 mb-1">
              <FileText className="w-4 h-4" />
              <span className="text-xs">Wild Texts</span>
            </div>
            <p className="text-2xl font-bold text-gray-100">{stats.userTextsSubmitted}</p>
          </div>
        </div>
      )}

      {/* Course Link */}
      {stats && stats.completed === 0 && (
        <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <BookOpen className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-300">New to Close Reading?</h3>
              <p className="text-sm text-gray-400 mt-1">
                Take the "Reading AI Closely" course to learn the methodology before practicing on your own texts.
              </p>
              <Link
                to="/courses/close-reading"
                className="inline-block mt-2 text-sm text-blue-400 hover:text-blue-300"
              >
                Start the course →
              </Link>
            </div>
          </div>
        </div>
      )}

      {showSubmitForm && (
        <form onSubmit={handleSubmitText} className="bg-gray-800 rounded-lg p-6 mb-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              AI-Generated Text *
            </label>
            <textarea
              value={formData.aiText}
              onChange={(e) => setFormData({ ...formData, aiText: e.target.value })}
              placeholder="Paste the AI output you want to analyze..."
              className="w-full h-40 bg-gray-900 border border-gray-700 rounded-lg p-3 text-gray-200 placeholder-gray-500"
              required
              minLength={50}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                AI Model (optional)
              </label>
              <input
                type="text"
                value={formData.aiModel}
                onChange={(e) => setFormData({ ...formData, aiModel: e.target.value })}
                placeholder="e.g., ChatGPT-4, Claude, Gemini"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg p-3 text-gray-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Where did you find this? (optional)
              </label>
              <input
                type="text"
                value={formData.sourceDescription}
                onChange={(e) => setFormData({ ...formData, sourceDescription: e.target.value })}
                placeholder="e.g., Work email, homework help, code review"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg p-3 text-gray-200"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Original Prompt (optional)
            </label>
            <input
              type="text"
              value={formData.aiPrompt}
              onChange={(e) => setFormData({ ...formData, aiPrompt: e.target.value })}
              placeholder="What was the AI asked?"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg p-3 text-gray-200"
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowSubmitForm(false)}
              className="px-4 py-2 text-gray-400 hover:text-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || formData.aiText.length < 50}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-4 py-2 rounded-lg"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Start Analysis
            </button>
          </div>
        </form>
      )}

      {/* User's submitted texts */}
      {userTexts.length > 0 ? (
        <div className="space-y-3">
          <h2 className="text-lg font-medium text-gray-300">Your Submitted Texts</h2>
          {userTexts.map((text) => (
            <button
              key={text.id}
              onClick={() => handleSelectText(text.id)}
              className="w-full text-left bg-gray-800 hover:bg-gray-750 rounded-lg p-4 transition-colors"
            >
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-gray-500 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-200 truncate">
                      {text.sourceDescription || 'Untitled'}
                    </span>
                    {text.aiModel && (
                      <span className="text-xs bg-gray-700 px-2 py-0.5 rounded">
                        {text.aiModel}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 truncate mt-1">
                    {text.aiText?.substring(0, 100)}...
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    {new Date(text.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      ) : !showSubmitForm && (
        <div className="text-center py-12 bg-gray-800 rounded-lg">
          <FileText className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-300">No texts yet</h3>
          <p className="text-gray-500 mt-1 mb-4">
            Submit an AI-generated text to start practicing
          </p>
          <button
            onClick={() => setShowSubmitForm(true)}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Your First Text
          </button>
        </div>
      )}
    </div>
  );
}
